import type { ReplayEvent } from "../packages/db/src/public-types";

interface LiveResponse { live: boolean; now: number; events: ReplayEvent[] }

const POLL_MS = 20_000;
const OVERLAP_S = 180; // events land in D1 up to a minute or two after detection; ids de-duplicate the overlap

/**
 * Phase 7 live mode. While the Companies House companies stream is connected, the map polls /api/live and plays
 * each new birth or death within the next poll interval, instead of replaying 15-minute slots on a delay.
 */
export class LiveFeed {
  private seen = new Set<string>();
  private last = 0;
  private timer = 0;

  constructor(private readonly h: { onEvent(ev: ReplayEvent, animate: boolean): void; onDrop(): void }) {}

  /** Seeds the last hour as settled dots. Resolves false (and does nothing) if the stream isn't live. */
  async start(): Promise<boolean> {
    const first = await this.fetch(Math.floor(Date.now() / 1000) - 3600);
    if (!first?.live) return false;
    for (const ev of first.events) { this.seen.add(ev.id); this.h.onEvent(ev, false); }
    this.last = first.now;
    this.timer = window.setTimeout(() => void this.tick(), POLL_MS);
    return true;
  }

  private async fetch(after: number): Promise<LiveResponse | null> {
    const res = await fetch(`/api/live?after=${after}`).catch(() => null);
    return res?.ok ? ((await res.json()) as LiveResponse) : null;
  }

  private async tick(): Promise<void> {
    const r = await this.fetch(this.last - OVERLAP_S);
    if (r && !r.live) { this.h.onDrop(); return; }
    if (r) {
      const fresh = r.events.filter((ev) => !this.seen.has(ev.id));
      if (this.seen.size > 20_000) this.seen.clear();
      // Spread the new dots across the next interval so they trickle rather than land in a clump.
      for (const ev of fresh) {
        this.seen.add(ev.id);
        window.setTimeout(() => this.h.onEvent(ev, !document.hidden), Math.random() * POLL_MS);
      }
      this.last = r.now;
    }
    this.timer = window.setTimeout(() => void this.tick(), POLL_MS);
  }

  stop(): void { window.clearTimeout(this.timer); }
}
